"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface SpeechRecognitionAlternativeLike {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionResultListLike {
  length: number;
  [index: number]: SpeechRecognitionResultLike;
}

interface SpeechRecognitionEventLike extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
}

interface SpeechRecognitionErrorEventLike extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: ((event: Event) => void) | null;
  onstart: ((event: Event) => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export type SpeechMode = "append" | "sendOnStop";

export interface UseWebSpeechToTextOptions {
  /**
   * append: each finalized segment is passed to onFinalTranscript (dictation).
   * sendOnStop: segments are buffered and onSendTranscript fires once when listening ends.
   */
  mode?: SpeechMode;
  /** Called once per finalized speech segment (append mode). */
  onFinalTranscript?: (text: string) => void;
  /** Called once when listening stops with the combined transcript (sendOnStop mode). */
  onSendTranscript?: (text: string) => void;
  /** BCP-47 language tag. Defaults to "en-US". */
  lang?: string;
}

export interface UseWebSpeechToTextResult {
  isSupported: boolean;
  isListening: boolean;
  interimTranscript: string;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

export function useWebSpeechToText({
  mode = "append",
  onFinalTranscript,
  onSendTranscript,
  lang = "en-US",
}: UseWebSpeechToTextOptions): UseWebSpeechToTextResult {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const onFinalRef = useRef(onFinalTranscript);
  const onSendRef = useRef(onSendTranscript);
  const modeRef = useRef(mode);
  const bufferRef = useRef("");
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    onFinalRef.current = onFinalTranscript;
  }, [onFinalTranscript]);

  useEffect(() => {
    onSendRef.current = onSendTranscript;
  }, [onSendTranscript]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    setIsSupported(getRecognitionCtor() !== null);
  }, []);

  const ensureRecognition = useCallback((): SpeechRecognitionLike | null => {
    if (recognitionRef.current) return recognitionRef.current;
    const Ctor = getRecognitionCtor();
    if (!Ctor) return null;

    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setInterimTranscript("");
      bufferRef.current = "";
    };

    recognition.onresult = (event) => {
      let interim = "";
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? "";
        if (result.isFinal) {
          finalText += transcript;
        } else {
          interim += transcript;
        }
      }
      if (finalText) {
        const trimmed = finalText.trim();
        if (trimmed) {
          if (modeRef.current === "sendOnStop") {
            bufferRef.current = bufferRef.current
              ? `${bufferRef.current.trimEnd()} ${trimmed}`
              : trimmed;
          } else {
            onFinalRef.current?.(trimmed);
          }
        }
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = () => {
      setIsListening(false);
      setInterimTranscript("");
      bufferRef.current = "";
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript("");
      if (modeRef.current === "sendOnStop") {
        const combined = bufferRef.current.trim();
        bufferRef.current = "";
        if (combined) {
          onSendRef.current?.(combined);
        }
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [lang]);

  const start = useCallback(() => {
    const recognition = ensureRecognition();
    if (!recognition) return;
    try {
      recognition.start();
    } catch {
      // Calling start() while already started throws InvalidStateError; ignore.
    }
  }, [ensureRecognition]);

  const stop = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    try {
      recognition.stop();
    } catch {
      // Stopping when not started throws; ignore.
    }
  }, []);

  const toggle = useCallback(() => {
    if (isListening) {
      stop();
    } else {
      start();
    }
  }, [isListening, start, stop]);

  useEffect(() => {
    return () => {
      const recognition = recognitionRef.current;
      if (!recognition) return;
      try {
        recognition.abort();
      } catch {
        // Ignore abort errors during unmount.
      }
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.onstart = null;
      recognitionRef.current = null;
    };
  }, []);

  return {
    isSupported,
    isListening,
    interimTranscript,
    start,
    stop,
    toggle,
  };
}
